import { CrownTwoTone, Highlighter } from 'core/components/ui';
import type { MyUserViewModel } from 'core/services';
import { t } from 'i18next';
import styled from 'styled-components';
import type { Column, Columns } from '../types';

type UseColumns = (v: {
  searchText: string;
  user: MyUserViewModel | null;
  renderRoles: Column['render'];
}) => Columns;

const SMeIcon = styled(CrownTwoTone)`
  margin-left: 5px;
`;

const roleOrder: Record<string, number> = {
  admin: 1,
  owner: 2,
  member: 3,
  guest: 4,
  null: 5,
  undefined: 6,
};

const sortDirections: ('ascend' | 'descend')[] = ['ascend', 'descend'];
const defaultSortOrder = 'ascend';

const getRoleValue = (role: string | null | undefined): string => {
  if (role === null) return 'null';
  if (role === undefined) return 'undefined';
  return role;
};

export const useColumns: UseColumns = ({ renderRoles, searchText, user }) => {
  const renderHighlighter: Column['render'] = (text: string) => (
    <Highlighter text={text} searchText={searchText} />
  );

  return [
    {
      title: t('Users.FirstName'),
      dataIndex: 'firstName',
      key: 'firstName',
      defaultSortOrder,
      sortDirections,
      sorter: {
        compare: (a, b) => {
          if (a.firstName === undefined) return -1;
          if (b.firstName === undefined) return 1;

          return a.firstName.localeCompare(b.firstName);
        },
        multiple: 1,
      },
      render(text, row, index) {
        const highlight = renderHighlighter(text, row, index);
        const me = row.id === user?.id ? <SMeIcon /> : null;

        return (
          <>
            {highlight}
            {me}
          </>
        );
      },
    },
    {
      title: t('Users.Email'),
      dataIndex: 'email',
      key: 'email',
      render: renderHighlighter,
      defaultSortOrder,
      sortDirections,
      sorter: {
        compare: (a, b) => a.email.localeCompare(b.email),
        multiple: 2,
      },
    },
    {
      title: t('Users.Role'),
      dataIndex: 'role',
      key: 'role',
      render: renderRoles,
      defaultSortOrder,
      sortDirections,
      sorter: {
        compare: (a, b, sortOrder) => {
          // Set logged user on top when we use sorted 'ascend' and 'descend'. It does not work for the status of sorting null
          const uid = user?.id;
          if (sortOrder === 'ascend') {
            if (a.id === uid) return -1;
            if (b.id === uid) return 1;
          } else {
            if (b.id === uid) return -1;
            if (a.id === uid) return 1;
          }

          const roleA = getRoleValue(a.role);
          const roleB = getRoleValue(b.role);

          if (roleOrder[roleA] && roleOrder[roleB]) return roleOrder[roleA] - roleOrder[roleB];

          if (roleOrder[roleA] !== undefined) return -1;
          if (roleOrder[roleB] !== undefined) return 1;

          return 0;
        },
        multiple: 3,
      },
    },
  ];
};
